import { scoreColour, scoreBandLabel } from "./score-colour";
import { CONDITIONS_STALE_HOURS } from "./refresh-policy";
import type { FactorConfidence } from "./factor-explanations";
import type { ConditionSafetyState } from "./safety-notes";

export const ATLAS_SPORTS = [
  { key: "piste", label: "Piste skiing", family: "Snow" },
  { key: "off-piste", label: "Off-piste", family: "Snow" },
  { key: "touring", label: "Ski touring", family: "Snow" },
  { key: "mtb", label: "Mountain biking", family: "Trail" },
  { key: "surf", label: "Surfing", family: "Surf" },
  { key: "bodyboarding", label: "Bodyboarding", family: "Surf" },
  { key: "wind", label: "Kite & windsurf", family: "Wind" },
  { key: "wing-foil", label: "Wing foiling", family: "Wind" },
  { key: "foil", label: "Prone foiling", family: "Surf" },
  { key: "downwind-foil", label: "Downwind foiling", family: "Wind" },
  { key: "diving", label: "Diving", family: "Water" },
  { key: "swimming", label: "Open-water swimming", family: "Water" },
  { key: "sup", label: "Paddleboarding", family: "Water" },
  { key: "sup-touring", label: "SUP touring", family: "Water" },
  { key: "sup-surf", label: "SUP surfing", family: "Surf" },
  { key: "sup-downwind", label: "SUP downwind", family: "Wind" },
  { key: "sailing-club", label: "Club sailing", family: "Sail" },
  { key: "sailing-hp", label: "Performance sailing", family: "Sail" },
] as const;
export type AtlasSport = typeof ATLAS_SPORTS[number]["key"];
export type AtlasMode = AtlasSport | "overview";
export type AtlasScope = "ski" | "surf" | "sailing";
export const ATLAS_SCOPES: readonly AtlasScope[] = ["ski", "surf", "sailing"];
export const ATLAS_DAYS = 7;
export const ATLAS_MAX_SHARDS = 32;
export const ATLAS_SHARD_BYTES = 256 * 1024;
export const ATLAS_LOCATION_MAX_BYTES = 48 * 1024;
export const ATLAS_SOURCE_PAGE_ROWS = 16;

// Tuple positions are versioned. Scores stay on the canonical 0..10 scale.
export type AtlasReading = [sport: AtlasSport, score: number, confidence: FactorConfidence,
  safety: ConditionSafetyState, window: [number, number] | null, evidence: string | null];
export type AtlasWeather = [code: number | null, snowCm: number | null,
  windKmh: number | null, windFromDeg: number | null, swellM: number | null,
  periodS: number | null, swellFromDeg: number | null, minC: number | null,
  maxC: number | null, waterC: number | null, rainMm: number | null];
export type AtlasDay = { date: string; w: AtlasWeather; s: AtlasReading[]; source?: "modelled-past"; depthCm?: number | null; freezingM?: number | null; cloudPct?: number | null };
export type AtlasLocation = {
  v: 1; id: string; slug: string; name: string; country: string; region: string;
  type: AtlasScope; lat: number; lng: number; updatedAt: string;
  scoringVersion: number; days: AtlasDay[];
  /** Completed local calendar days, without reconstructed or incompatible scores. */
  history?: AtlasDay[];
  elevationM?: number | null;
};
export type AtlasDataset = {
  locations: AtlasLocation[];
  publishedAt: string | null;
  unavailableScopes: AtlasScope[];
  namedSup: boolean;
};
export type AtlasManifest = {
  v: 1; scope: AtlasScope; date: string; keys: string[]; publishedAt: string;
};

export function isAtlasSport(value: string): value is AtlasSport {
  return ATLAS_SPORTS.some((sport) => sport.key === value);
}
export function atlasSportLabel(sport: AtlasMode): string {
  return sport === "overview" ? "Overview" : ATLAS_SPORTS.find((item) => item.key === sport)?.label ?? sport;
}
export function atlasDay(location: AtlasLocation, date: string): AtlasDay | undefined {
  return location.history?.find((day) => day.date === date) ?? location.days.find((day) => day.date === date);
}
export function atlasDayIsStale(location: AtlasLocation, date: string, now = Date.now()): boolean {
  return atlasDay(location, date)?.source === "modelled-past" ? false : atlasIsStale(location, now);
}
/** A missing assessment must not erase a known surf beach or ski resort. */
export function atlasLocationSupportsMode(location: AtlasLocation, mode: AtlasMode): boolean {
  return mode === "overview"
    || location.days.some((day) => day.s.some((reading) => reading[0] === mode))
    || (mode === "surf" && location.type === "surf")
    || (["piste", "off-piste", "touring"].includes(mode) && location.type === "ski")
    || (mode.startsWith("sailing-") && location.type === "sailing");
}
export function atlasReading(day: AtlasDay | undefined, mode: AtlasMode): AtlasReading | undefined {
  if (mode === "overview") return day?.s.slice().sort((a, b) => b[1] - a[1])[0];
  return day?.s.find((reading) => reading[0] === mode);
}
export function atlasDates(start: string): string[] {
  const date = new Date(`${start}T12:00:00Z`);
  return Array.from({ length: ATLAS_DAYS }, (_, i) => new Date(date.getTime() + i * 86400000).toISOString().slice(0, 10));
}
export function atlasScoreColour(score: number | null | undefined): string {
  return scoreColour(score ?? null);
}
export function atlasScoreLabel(score: number | null | undefined): string {
  return scoreBandLabel(score ?? null) ?? "No score";
}
export function atlasWeatherLabel(w: AtlasWeather): string {
  if (w[0] !== null && w[0] >= 95) return "Thunderstorms";
  if (w[1] !== null && w[1] > 0.2) return `${Math.round(w[1] * 10) / 10} cm fresh snow`;
  if (w[0] !== null && [71, 73, 75, 77, 85, 86].includes(w[0])) return "Snow showers";
  if (w[0] !== null && w[0] >= 51 && w[0] <= 82) return "Rain or showers";
  if (w[0] !== null && w[0] <= 1) return "Clear skies";
  if (w[0] === 2) return "Partly cloudy";
  if (w[0] === 3) return "Overcast";
  if (w[0] === 45 || w[0] === 48) return "Fog";
  return "Weather unavailable";
}
export function atlasIsStale(location: AtlasLocation, now = Date.now()): boolean {
  return !Number.isFinite(Date.parse(location.updatedAt)) || now - Date.parse(location.updatedAt) > CONDITIONS_STALE_HOURS * 3600000 || Date.parse(location.updatedAt) - now > 15 * 60000;
}
export function atlasWindowLabel(window: [number, number]): string {
  return window.map((hour) => `${String(hour).padStart(2, "0")}:00`).join(" to ");
}

export function atlasWeekWeather(location: AtlasLocation, dates: readonly string[]): string[] {
  const days = dates.flatMap((date) => { const day = atlasDay(location, date); return day ? [day] : []; });
  const notes: string[] = [];
  const snow = days.filter((day) => day.w[1] !== null);
  const snowy = snow.filter((day) => day.w[1]! > 0.2);
  if (snowy.length) notes.push(`${Math.round(snow.reduce((sum, day) => sum + day.w[1]!, 0))} cm of new snow across ${snowy.length} ${snowy.length === 1 ? "day" : "days"}, from ${snow.length} ${snow.length === 1 ? "day" : "days"} with snowfall data.`);
  const swell = days.flatMap((day) => day.w[4] === null ? [] : [day.w[4]]);
  if (swell.length >= 2) notes.push(`Swell ranges from ${Math.min(...swell).toFixed(1)} to ${Math.max(...swell).toFixed(1)} m across ${swell.length} forecast days.`);
  const skies = days.filter((day) => day.w[0] !== null);
  const clear = skies.filter((day) => day.w[0]! <= 1).length;
  if (skies.length >= 3 && clear) notes.push(`${clear} of ${skies.length} weather forecasts show clear skies.`);
  const windy = days.filter((day) => (day.w[2] ?? 0) >= 35);
  if (windy.length) notes.push(`Strong wind is forecast on ${windy.length} ${windy.length === 1 ? "day" : "days"}. Check the daily wind and safety detail.`);
  return notes.slice(0, 3);
}
